/* ============================================================
   LAMEEM — Story page behaviour
   File: assets/lameem-story.js

   Two behaviours only, both progressive enhancements:
     1. Scroll reveal  — class-only, never writes inline styles
     2. Stat count-up  — animates the numbers in the stats band

   Guarded against double execution, because both story sections
   emit this same <script> tag and the browser will run the file
   once per tag even though it only downloads it once.
   ============================================================ */
(function () {
  'use strict';

  if (window.__lameemStoryInit) return;
  window.__lameemStoryInit = true;

  var REDUCED = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  var SUPPORTED = 'IntersectionObserver' in window;

  /* ── Reveal ─────────────────────────────────────────────────
     `.lm-js` is added here rather than sitting in the markup, so
     with JavaScript disabled the CSS that hides `.lm-reveal`
     never applies and the whole page is readable. Same reason the
     class is added per-root instead of on <html>: a section that
     fails to load leaves its own content visible.
     ───────────────────────────────────────────────────────── */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;

    revealObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    return revealObserver;
  }

  function initReveal(root) {
    var items = root.querySelectorAll('.lm-reveal');
    if (!items.length) return;

    if (!SUPPORTED || REDUCED.matches) {
      // No `.lm-js`, so the hidden state is never applied at all.
      return;
    }

    root.classList.add('lm-js');

    var observer = getRevealObserver();
    items.forEach(function (item) {
      if (item.classList.contains('is-in')) return;
      observer.observe(item);
    });
  }

  /* ── Stat count-up ──────────────────────────────────────────
     Values are authored as plain text ("12", "48h", "100%") and
     the digits are read back out of the DOM, so the rendered
     number is always what the merchant typed — suffixes, prefixes
     and non-numeric values ("Woman-led") all survive untouched.
     ───────────────────────────────────────────────────────── */
  function parseValue(text) {
    var match = String(text).match(/^(\D*?)(\d[\d,.\s]*)(.*)$/);
    if (!match) return null;

    var digits = match[2].replace(/[,\s]/g, '');
    var target = parseFloat(digits);
    if (isNaN(target)) return null;

    var dot = digits.indexOf('.');
    return {
      prefix: match[1],
      suffix: match[3],
      target: target,
      decimals: dot === -1 ? 0 : digits.length - dot - 1
    };
  }

  function countUp(el) {
    var parsed = parseValue(el.textContent.trim());
    if (!parsed) return;

    var duration = 1100;
    var start = null;
    var group = parsed.target >= 1000;

    function frame(now) {
      if (start === null) start = now;

      var progress = Math.min((now - start) / duration, 1);
      // easeOutExpo — fast off the line, long settle
      var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      var current = (parsed.target * eased).toFixed(parsed.decimals);

      if (group) {
        current = Number(current).toLocaleString();
      }

      el.textContent = parsed.prefix + current + parsed.suffix;

      if (progress < 1) window.requestAnimationFrame(frame);
    }

    window.requestAnimationFrame(frame);
  }

  function initStats(root) {
    var stats = root.querySelectorAll('[data-lm-count]');
    if (!stats.length || !SUPPORTED || REDUCED.matches) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countUp(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.6 }
    );

    stats.forEach(function (stat) { observer.observe(stat); });
  }

  /* ── Boot ─────────────────────────────────────────────────── */
  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-lm-story]');
    roots.forEach(function (root) {
      if (root.dataset.lmReady === 'true') return;
      root.dataset.lmReady = 'true';
      initReveal(root);
      initStats(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor: only re-scan the subtree that actually changed,
     so editing one section can't re-hide content elsewhere. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
